import { CreateImageRequest, GetPointResponse, GetTopicResponse, Image, ImageType, ImageUpload, ImageWithType } from '../../types';
import { addPointImage, deletePointImage, getPointImages, getPointPlaceDetails } from '../api/points-api';

import { arrayBufferToBase64 } from '../utils';
import { getMainPage } from '../page-name';

/**
 * A linked listing's native photos are synthesized server-side onto the
 * point's `custom_field_entries` (see `fetch::listing_photos_field_id`) — no
 * field definition backs it, so this is detected by its `{ urls: [...] }`
 * shape rather than by matching a `field_id` against the topic's schema.
 */
function getLinkedListingPhotoUrls(point?: GetPointResponse): string[] {
    const entry = point?.custom_information?.custom_field_entries?.find((e) => {
        const value = e.field_value_json as { urls?: unknown } | undefined;
        return Array.isArray(value?.urls);
    });
    const urls = (entry?.field_value_json as { urls?: unknown } | undefined)?.urls;
    return Array.isArray(urls) ? urls.filter((url): url is string => typeof url === 'string') : [];
}

function dedupeByImageUrl(images: ImageWithType[]): ImageWithType[] {
    const seen = new Set<string>();
    return images.filter((image) => {
        if (seen.has(image.image)) {
            return false;
        }
        seen.add(image.image);
        return true;
    });
}

export async function getImageUrls(point: GetPointResponse, topic?: GetTopicResponse, point_key?: string, amplitude_callsite?: string): Promise<ImageWithType[]> {
    const userImages = await getPointImages(amplitude_callsite, topic?._id.$oid, point?._id.$oid, point_key) as Image[];
    const userImagesUrls = userImages.map((image) => {
        return { image: image.sas_url, type: ImageType.User };
    });

    // Fallback only: the linked listing's own photos, used when the point
    // has none of its own to show.
    const listingImageUrls = userImagesUrls.length === 0
        ? getLinkedListingPhotoUrls(point).map((image) => ({ image, type: ImageType.Internal }))
        : [];

    const googleImageDetails = topic?.disable_google_images === false
        ? await getPointPlaceDetails(
            amplitude_callsite,
            topic?._id.$oid,
            point?._id.$oid,
            point_key
        )
        : undefined;
    const googleImageUrls = googleImageDetails?.photos !== undefined ? googleImageDetails?.photos.map((image: string) => {
        return { image, type: ImageType.Google };
    }) : [];

    // The listing's photos and Google's scraped photos for the same place can
    // be the exact same image URL — drop later duplicates rather than show it twice.
    return dedupeByImageUrl([...userImagesUrls, ...listingImageUrls, ...googleImageUrls]);
}

// use a point key if it exists as a QP, otherwise use the topic key
export async function processPointImages(imagesToUpload: ImageUpload[], imagesToDelete: Image[], pointId: string, topicId: string, topicOrPointKey: string, amplitudeCallsite: string): Promise<boolean> {
    if (imagesToUpload.length > 0) {
        try {
            for (const picture of imagesToUpload) {
                if (picture.file.size > 15728640) {
                    getMainPage().errorToastElement?.showToastAndThrow(
                        'The image you uploaded is too large.  Please upload a smaller image.'
                    );
                }

                const imageRequest: CreateImageRequest = {
                    name: picture.file.name,
                    data: arrayBufferToBase64(await picture.file.arrayBuffer()),
                };

                await addPointImage(
                    amplitudeCallsite,
                    topicId,
                    pointId,
                    imageRequest,
                    topicOrPointKey,
                    picture.orientation
                );
            }
        } catch (_) {
            getMainPage().errorToastElement.showToastAndThrow('An error has occurred.  Context: an image upload has failed, but you can try again by editing the point from your email.');
            return false;
        }

        await getPointImages(
            amplitudeCallsite,
            topicId,
            pointId,
            topicOrPointKey,
            false
        );
    }

    if (imagesToDelete.length > 0) {
        try {
            const imagePromises = imagesToDelete.map(async (picture) => {
                await deletePointImage(
                    amplitudeCallsite,
                    topicId,
                    pointId,
                    picture._id.$oid,
                    topicOrPointKey
                );
            });

            await Promise.all(imagePromises);
        } catch (_) {
            getMainPage().errorToastElement.showToastAndThrow('An error has occurred.  Context: an image deleting has failed, but you can try again by editing the point from your email.');
            return false;
        }

        await getPointImages(
            amplitudeCallsite,
            topicId,
            pointId,
            topicOrPointKey,
            false
        );
    }

    return true;
}
